/**
 * Utilidades del roomCatalog de Parques (additionalInfo.roomCatalog, armado por Octopus).
 *
 * roomTypes[].packages[código] y roomTypes[].accessibility[].offers son listas de ofertas, una por proveedor.
 * Cada oferta apunta, por rateKey, a la habitación real de additionalInfo.rooms[]: de ahí salen el precio con
 * rentabilidad, el bookParam, la política de cancelación, las imágenes y el logo del proveedor. El precio crudo
 * del catálogo (offer.price) no trae rentabilidad y solo se usa si la habitación real no está en la respuesta.
 *
 * Código de paquete = [H|T] (T = incluye tickets) + [N|Q|T|D] (plan de comidas).
 */

export interface IParkProvider {
  id: string;
  name: string;
  logo: string;
}

export class ParkCatalogIndex {
  private byRateKey = new Map<string, any>();

  constructor(rooms: any[]) {
    (rooms || []).forEach((r: any) => {
      const key = r?.room?.rateKey;
      if (key && !this.byRateKey.has(key)) {
        this.byRateKey.set(key, r);
      }
    });
  }

  real(offer: any): any {
    return offer ? this.byRateKey.get(offer.rateKey) || null : null;
  }

  // Habitación real de una tarifa que solo se conoce tras un checkAvail (planes de comidas de Full of Dreams).
  set(rateKey: string, real: any): void {
    this.byRateKey.set(rateKey, real);
  }

  // Precio con rentabilidad (habitación real); si no está, el precio crudo del catálogo.
  price(offer: any): number {
    const real = this.real(offer)?.room;
    if (real?.optionNightsNetTotalProf != null) {
      return Number(real.optionNightsNetTotalProf) + Number(real?.pricing?.taxesExcluded || 0);
    }
    return Number(offer?.price || 0);
  }

  currency(offer: any): string {
    return this.real(offer)?.room?.pricing?.currency || 'USD';
  }

  provider(offer: any): IParkProvider {
    const real = this.real(offer);
    return {
      id: String(offer?.providerId || real?.provider?.id || ''),
      name: offer?.providerName || real?.provider?.name || '',
      logo: offer?.providerLogo || real?.provider?.logo || '',
    };
  }

  image(offer: any): string {
    return this.real(offer)?.room?.images?.[0] || '';
  }

  isRefundable(offer: any): boolean {
    return this.real(offer)?.room?.rateClass === 'R';
  }

  cheapest(offers: any[]): any {
    const list = (offers || []).filter(o => !!this.real(o));
    return list.length ? [...list].sort((a, b) => this.price(a) - this.price(b))[0] : null;
  }
}

export function packagesOf(roomType: any): { [code: string]: any[] } {
  const p = roomType?.packages;
  return p && !Array.isArray(p) ? p : {};
}

// Oferta de un proveedor dentro de una lista de ofertas.
export function offerOfProvider(offers: any[], providerId: string): any {
  return (offers || []).find(o => String(o?.providerId) === String(providerId)) || null;
}

// Todas las ofertas (de cualquier paquete) de un proveedor para el tipo de habitación.
export function offersOfProvider(roomType: any, providerId: string): any[] {
  return Object.keys(packagesOf(roomType))
    .map(code => offerOfProvider(packagesOf(roomType)[code], providerId))
    .filter(o => !!o);
}

// Proveedores que ofrecen el tipo de habitación (en paquetes o en variantes accesibles).
export function providersOf(roomType: any, index: ParkCatalogIndex): IParkProvider[] {
  const seen = new Map<string, IParkProvider>();
  const note = (offer: any) => {
    const p = index.provider(offer);
    if (p.id && !seen.has(p.id)) {
      seen.set(p.id, p);
    }
  };
  Object.values(packagesOf(roomType)).forEach((offers: any[]) => (offers || []).forEach(note));
  (roomType?.accessibility || []).forEach((a: any) => (a?.offers || []).forEach(note));
  return Array.from(seen.values());
}

export const PLAN_ORDER = ['N', 'Q', 'T', 'D'];

// diningPlans[].level del checkAvail -> código de plan.
export const PLAN_OF_LEVEL: { [level: string]: string } = { none: 'N', quick: 'Q', table: 'T', deluxe: 'D' };

export const PLAN_NAMES: { [plan: string]: string } = {
  N: 'Sin plan de comidas',
  Q: 'Plan de comidas de servicio rápido',
  T: 'Plan de comidas con servicio de mesa',
  D: 'Plan de comidas Deluxe',
};

export const ACCESSIBILITY_LABELS: { [kind: string]: string } = {
  hearing: 'Auditiva (alarmas visuales)',
  wheelchair_rollin: 'Silla de ruedas · ducha a ras de piso',
  wheelchair_tub: 'Silla de ruedas · bañera',
  florida_special: 'Accesible especial Florida',
};

/** Proveedor que vende los tickets de Universal como add-on del hotel (Full of Dreams). */
export const FULL_OF_DREAMS_ID = 'FOD123456';

/**
 * Precio del ticket para una fecha de inicio. En los tickets add-on (Universal) cada fecha disponible trae su
 * precio por adulto y por niño; se les aplica la misma rentabilidad que el back puso en ticketPrice (ticketPriceProf).
 */
export function ticketPriceOn(option: any, date: string | null, adults: number, children: number): number {
  const base = Number(option?.ticketPriceProf ?? option?.ticketPrice ?? 0);
  const raw = Number(option?.ticketPrice);
  const entry = date ? (option?.dates || []).find((d: any) => String(d?.date || '').substring(0, 10) === date) : null;
  if (!entry || !(raw > 0)) {
    return base;
  }
  const price = Number(entry.adultPrice ?? 0) * adults + Number(entry.childPrice ?? 0) * children;
  return Math.round(price * (base / raw) * 100) / 100;
}

/**
 * Código de paquete ([H|T] + [N|Q|T|D]) a partir del texto de una tarifa de Action Travel:
 * "Habitación : (Room + Ticket + Quick-Service Dining Plan)". Sin " : " (Full of Dreams) es solo hotel.
 */
export function packageCodeOfRoomType(roomType: string): string {
  const text = String(roomType || '');
  const separator = text.indexOf(' : ');
  if (separator < 0) {
    return 'HN';
  }
  const pkg = text.substring(separator + 3);
  const ticket = /ticket/i.test(pkg) ? 'T' : 'H';
  const meal = /deluxe/i.test(pkg) ? 'D' : /table/i.test(pkg) ? 'T' : /quick/i.test(pkg) ? 'Q' : 'N';
  return ticket + meal;
}

const shortRoomCode = (code: any): string => String(code || '').split('||')[0].trim();

/**
 * Paquetes de una variante accesible. El catálogo solo trae su tarifa más barata por proveedor, sin decir de qué
 * paquete es; se reconstruyen con las habitaciones reales de ese proveedor que tienen el mismo código de habitación
 * (el de la oferta y los de mergedCodes). Devuelve { código de paquete: [oferta] } como roomType.packages.
 */
export function variantPackages(offer: any, providerId: string, rooms: any[]): { [code: string]: any[] } {
  if (!offer) {
    return {};
  }
  const codes = [offer.roomCode, ...(offer.mergedCodes || [])].map(shortRoomCode).filter(Boolean);
  const packages: { [code: string]: any[] } = {};
  (rooms || []).forEach((real: any) => {
    const room = real?.room;
    if (!room?.rateKey || String(real?.provider?.id) !== String(providerId) || !codes.includes(shortRoomCode(room.roomCode))) {
      return;
    }
    const code = packageCodeOfRoomType(room.roomType);
    const price = Number(room.pricing?.priceWithTax ?? 0);
    const current = packages[code]?.[0];
    if (!current || price < current.price) {
      packages[code] = [{
        rateKey: room.rateKey,
        providerId: String(providerId),
        providerName: real.provider?.name || '',
        providerLogo: real.provider?.logo || '',
        roomCode: room.roomCode,
        requiresCheckAvail: room.requiresCheckAvail === true,
        price,
        cancelFrom: real.cancellationPolicy?.[0]?.from || null,
        cheapest: true,
      }];
    }
  });
  return packages;
}
